/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 *
 * Core EOS service — handles study group loading, study info preselection,
 * user enrichment/sorting, site-user mapping, and building the startEos payload.
 *
 * Migrated from: app/view/app/eos/eos.service.js
 */

import { eosApiService } from '../api/api.eos'
import { usersApiService } from '../api/users'
import { EOS_CONFIG_LEVEL, EOS_NULL_UUID } from '../../constants/eos'
import type {
  EosConfig,
  EosOptions,
  EosStartRequest,
  EosStudyDto,
  EosSiteDto,
  EosUserDto,
  EosUser,
  EosSite,
  EosSitesMap,
  EosCountrySite,
  EosStudyGroup,
  EosStudyInfo,
  EosStudyInfoRaw,
  EosFolderPathItem
} from '../../types/eos'

// ---------------------------------------------------------------------------
// Helpers (used by getUuid / getIsMccEnabled)
// ---------------------------------------------------------------------------

/**
 * Returns the effective study UUID to use in API calls.
 * When MCC is enabled and an environment is selected, uses env UUID; otherwise study UUID.
 */
export function getUuid(config: EosConfig): string {
  return getIsMccEnabled(config) && config.environment ? config.environment.uuid : config.study!.uuid
}

/**
 * Returns whether MCC (multi-country configuration) is enabled for the study group.
 */
export function getIsMccEnabled(config: EosConfig): boolean {
  return !!(config && config.studyGroup && config.studyGroup.mccEnabled)
}

// ---------------------------------------------------------------------------
// User enrichment & sorting
// ---------------------------------------------------------------------------

/**
 * Postprocesses raw user data — sets isUser flag, fullName, and displayName.
 * Mutates users in-place.
 */
export function usersPostprocess(users: EosUser[]): void {
  for (const user of users) {
    const fullName = usersApiService.getFullName(user.firstName, user.lastName)
    Object.assign(user, {
      isUser: true,
      fullName,
      displayName: usersApiService.getDisplayName(fullName, user.email)
    })
  }
}

/**
 * Sorts users by lastName + firstName + email (case-insensitive).
 */
export function usersSort(users: EosUser[]): EosUser[] {
  return [...users].sort((a, b) => {
    const keyA =
      (a.lastName ? a.lastName.toLowerCase() : '') +
      (a.firstName ? a.firstName.toLowerCase() : '') +
      (a.email ? a.email.toLowerCase() : '')
    const keyB =
      (b.lastName ? b.lastName.toLowerCase() : '') +
      (b.firstName ? b.firstName.toLowerCase() : '') +
      (b.email ? b.email.toLowerCase() : '')
    return keyA.localeCompare(keyB)
  })
}

/**
 * Enriches users by postprocessing and sorting them.
 * Returns a new sorted array (original array is mutated for enrichment).
 */
export function enrichUsers(users: EosUser[]): EosUser[] {
  usersPostprocess(users)
  return usersSort(users)
}

// ---------------------------------------------------------------------------
// Site-user mapping
// ---------------------------------------------------------------------------

/**
 * Maps users to their respective sites based on the user's `sites` array.
 * Mutates the sitesMap in-place — each site gets its `users` array populated.
 * Returns the sites as an array.
 */
export function mapUsersToSites(users: EosUser[], sitesMap: EosSitesMap): EosSite[] {
  // Reset all site user arrays
  for (const site of Object.values(sitesMap)) {
    site.users = []
  }

  const usersWithSites = users.filter((user) => user.sites && user.sites.length > 0)

  for (const user of usersWithSites) {
    const checkMap: Record<string, boolean> = {}

    for (const site of user.sites!) {
      // Prevent duplicate user entries per site
      if (!checkMap[site.uuid]) {
        checkMap[site.uuid] = true
        const matchingSite = sitesMap[site.uuid]
        if (matchingSite) {
          matchingSite.users!.push(user)
        }
      } else {
        console.log('duplicate !', site, user)
      }
    }
  }

  return Object.values(sitesMap)
}

// ---------------------------------------------------------------------------
// Study groups
// ---------------------------------------------------------------------------

/**
 * Loads and merges study groups from both Plinth and internal APIs.
 * Plinth groups take precedence; internal-only groups are appended.
 * Returns groups sorted by name.
 */
export async function getStudyGroups(): Promise<EosStudyGroup[]> {
  const [internalResp, plinthResp] = await Promise.all([
    eosApiService.getStudyGroups(),
    eosApiService.getPlinthStudyGroups()
  ])

  const plinthStudyGroups = plinthResp as EosStudyGroup[]
  const plinthUuids = new Set(plinthStudyGroups.map((g) => g.uuid))

  const internalGroups = (internalResp as { elements: EosStudyGroup[] }).elements ?? []
  const internalOnlyGroups = internalGroups.filter((g) => !plinthUuids.has(g.uuid))

  const merged = [...plinthStudyGroups, ...internalOnlyGroups]
  return merged.sort((a, b) => (a.name ?? '').toLowerCase().localeCompare((b.name ?? '').toLowerCase()))
}

// ---------------------------------------------------------------------------
// Study info (preselection)
// ---------------------------------------------------------------------------

/**
 * Loads study info for a given FTP context or study folder.
 * Returns transformed study info or throws if not found / invalid.
 */
export async function loadStudyInfo(ftpContextId: number | null, studyFolderId: number | null): Promise<EosStudyInfo> {
  if (!ftpContextId && !studyFolderId) {
    throw new Error('No ftpContextId or studyFolderId provided')
  }

  const resp = (await eosApiService.getStudyInfo(ftpContextId as number, studyFolderId as number)) as EosStudyInfoRaw

  if (resp && resp.studyGroupUuid && resp.studyGroupUuid !== EOS_NULL_UUID) {
    return {
      studyGroup: resp.studyGroupUuid,
      study: resp.studyUuid,
      environment: resp.studyEnvUuid,
      isLegacy: resp.legacy,
      inProgress: resp.inProgress
    }
  }

  throw new Error('Invalid study info response')
}

// ---------------------------------------------------------------------------
// Build startEos payload (the most critical method)
// ---------------------------------------------------------------------------

/**
 * Creates the DTO used to call startEos API.
 * Diffs isSelected/wasSelected flags to determine which users/sites need
 * access granted or revoked.
 */
export function createStartEosData(config: EosConfig, options: EosOptions): EosStartRequest {
  // --- Determine study-level entry (only for STUDY level) ---
  const study = config.level === EOS_CONFIG_LEVEL.STUDY ? options.sitesMap.study : null

  // --- Determine sites list ---
  const sites = (() => {
    switch (config.level) {
      case EOS_CONFIG_LEVEL.STUDY:
        return Object.values(options.sitesMap).filter((site) => site.uuid !== 'study' && site.users)
      case EOS_CONFIG_LEVEL.COUNTRY:
        return Object.values(options.countriesMap).reduce<EosCountrySite[]>((memo, country) => {
          const selectedSites = (country.sites ?? []).filter((s) => s.isSelected === true && s.wasSelected === false)
          const deselectedSites = (country.sites ?? []).filter((s) => s.isSelected === false && s.wasSelected === true)
          return [...memo, ...selectedSites, ...deselectedSites]
        }, [])
      default:
        return []
    }
  })()

  // --- Build the root folder full path ---
  const rootFolder = (() => {
    const folder = config.folder!
    const folderFullPathArray: string[] = [folder.name]

    if (folder.parent && folder.parent.id !== options.eosFolderId) {
      folderFullPathArray.push(folder.parent.name)
      if (folder.parent.path && folder.parent.path.length > 0) {
        let isAfterRoot = true
        const parentPathArray = [...folder.parent.path]
          .reverse()
          .reduce<string[]>((arr, pathItem: EosFolderPathItem) => {
            if (pathItem.id === options.eosFolderId) {
              isAfterRoot = false
            }
            if (isAfterRoot) {
              arr.push(pathItem.name)
            }
            return arr
          }, [])
        folderFullPathArray.push(...parentPathArray)
      }
    }

    return folderFullPathArray.reverse().join('/')
  })()

  // --- Build study DTO ---
  const studyDTO: EosStudyDto = {
    name: config.study!.name,
    mccEnabled: !!config.studyGroup!.mccEnabled,
    envUuid: config.studyGroup!.mccEnabled && config.environment?.uuid ? config.environment.uuid : null,
    configurationTypeUuid: config.study!.configurationTypeUuid,
    uuid: config.study!.uuid,
    grantAccessToUsers: study?.users
      ? createUserAccessList(
          study.users as EosUser[],
          (user) =>
            (user.isSelected && !user.wasSelected) ||
            (!!user.isDownloadSelected && !user.wasDownloadSelected) ||
            (!!user.isAcknowledgeSelected && !user.wasAcknowledgeSelected),
          true
        )
      : null,
    removeAccessFromUsers: study?.users
      ? createUserAccessList(study.users as EosUser[], (user) => !user.isSelected && !!user.wasSelected, false)
      : null,
    sites: sites.map((site) => {
      const siteDto: EosSiteDto = {
        name: site.name,
        uuid: site.uuid,
        grantAccessToUsers: (() => {
          switch (config.level) {
            case EOS_CONFIG_LEVEL.STUDY:
              return createUserAccessList(
                (site as EosSite).users ?? [],
                (user) =>
                  (user.isSelected && !user.wasSelected) ||
                  (!!user.isDownloadSelected && !user.wasDownloadSelected) ||
                  (!!user.isAcknowledgeSelected && !user.wasAcknowledgeSelected),
                true
              )
            case EOS_CONFIG_LEVEL.COUNTRY:
              return createUserAccessList(
                (site as EosCountrySite).isSelected ? [config.studyOwner as EosUser] : [],
                () => true,
                true
              )
            default:
              return []
          }
        })(),
        removeAccessFromUsers: (() => {
          switch (config.level) {
            case EOS_CONFIG_LEVEL.STUDY:
              return createUserAccessList(
                (site as EosSite).users ?? [],
                (user) => !user.isSelected && !!user.wasSelected,
                false
              )
            case EOS_CONFIG_LEVEL.COUNTRY:
              return createUserAccessList(
                (site as EosCountrySite).isSelected ? [] : [config.studyOwner as EosUser],
                () => true,
                false
              )
            default:
              return []
          }
        })()
      }
      return siteDto
    })
  }

  return {
    studyGroupName: config.studyGroup!.name,
    studyGroupUuid: config.studyGroup!.uuid,
    targetFolderId: config.folder!.parent!.id,
    workflowInitiatorId: (() => {
      switch (config.level) {
        case EOS_CONFIG_LEVEL.STUDY:
          return config.createNewWorkflows && config.workflowInitiator ? config.workflowInitiator.id : undefined
        case EOS_CONFIG_LEVEL.COUNTRY:
          return null
        default:
          return undefined
      }
    })(),
    level: config.level,
    rootFolder,
    studyDTO
  }
}

// ---------------------------------------------------------------------------
// Private helpers
// ---------------------------------------------------------------------------

/**
 * Filters users by predicate and maps them to EosUserDto.
 */
function createUserAccessList(
  userList: EosUser[],
  predicate: (user: EosUser) => boolean,
  grant: boolean
): EosUserDto[] {
  return userList.filter(predicate).map((user) => createUserDto(user, grant))
}

/**
 * Creates a single user DTO for the startEos request payload.
 */
function createUserDto(user: EosUser, grant: boolean): EosUserDto {
  return {
    email: user.email,
    firstName: user.firstName,
    lastName: user.lastName,
    uuid: user.uuid,
    grantAccess: grant,
    assignToWorkflow: !!user.isDownloadSelected || !!user.isAcknowledgeSelected,
    createDownloadTask: user.isDownloadSelected !== user.wasDownloadSelected ? !!user.isDownloadSelected : false,
    createAcknowledgementTask:
      user.isAcknowledgeSelected !== user.wasAcknowledgeSelected ? !!user.isAcknowledgeSelected : false
  }
}
